import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { ExportProgramName, ExportProject } from "@/lib/ClassExportProject";
import { ERR_CODE_4000, ERR_CODE_4004 } from "@/lib/api-constants";
import { removeBase64InProject } from "@/lib/project-utils";
import { timeSelector } from "@/lib/text-utils";
import { checkEnterpriseCompany } from "@/lib/workspace-util";
import { useGetEnvListQuery, useGetEnvQuery } from "@/redux/api/commonApi";
import dashboardApi from "@/redux/api/dashboardApi";
import { useGetTemplateQuery } from "@/redux/api/projectApi";
import { getDashBoardState, getProjectState } from "@/redux/store";
import { dashboardActions } from "@/redux/stores/dashboardStore";
import { projectActions } from "@/redux/stores/projectStore";
import axios from "axios";
import { AuthModalTemplate } from "next-auth-account";
import { useSession } from "next-auth/react";
import { Trans, useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { StyledModal } from "./Style";
import { MODAL_NAME } from "./constants";

type LoginModalProps = {
  isMobile?: boolean;
};

export const LoginModal = ({ isMobile = false }: LoginModalProps) => {
  const router = useRouter();
  const session = useSession();
  const project = useAppSelector((store) => getProjectState(store).project);
  const { data: templateData } = useGetTemplateQuery(project?.templateId);
  // console.log('project: ', project)
  const openedModal = useAppSelector(
    (store) => getProjectState(store).openedModal
  );
  const callbackAfterLogin = useAppSelector(
    (store) => getProjectState(store).callbackAfterLogin
  );
  const [currentStep, setCurrentStep] = useState<string>("");

  const [cookies, setCookie] = useCookies(["first_page_view_pricing"]);
  const { automationModalOpen } = useAppSelector(
    (store) => getProjectState(store) ?? false
  );
  const { workspaceModal } = useAppSelector(
    (store) => getDashBoardState(store) ?? false
  );
  const automationModalOpenRef = useRef<boolean>(automationModalOpen);

  useEffect(() => {
    automationModalOpenRef.current = automationModalOpen;
  }, [automationModalOpen]);

  const { decryptParams } = useAppSelector(
    (store) => getDashBoardState(store).workspaceInfo
  );

  const env = useGetEnvQuery(
    { key: "NEXTAUTH_SERVICE_ID" },
    {
      pollingInterval: 30 * 60 * 1000,
      refetchOnFocus: false,
      refetchOnMountOrArgChange: false,
      refetchOnReconnect: false,
      skip: false,
    }
  );

  const { t } = useTranslation("common");
  const { setOpenedModal } = projectActions;
  const dispatch = useAppDispatch();

  const [redirectPath, setRedirectPath] = useState("");
  const [inputReferral, setInputReferral] = useState("");

  const time = timeSelector(project);

  const accountUserInfo = useAppSelector(
    (store) => getDashBoardState(store).accountUserInfo
  );
  const [trigger, { data: accountUserInfoData }] =
    dashboardApi.endpoints.getAccountUserInfo.useLazyQuery();

  const { data: NEXTAUTH_URL } = useGetEnvQuery(
    { key: "NEXTAUTH_URL" },
    {
      pollingInterval: 30 * 60 * 1000,
      refetchOnFocus: false,
      refetchOnMountOrArgChange: false,
      refetchOnReconnect: false,
      skip: false,
    }
  );

  const { data: ACCOUNT_URL } = useGetEnvQuery(
    { key: "ACCOUNT_URL" },
    {
      pollingInterval: 30 * 60 * 1000,
      refetchOnFocus: false,
      refetchOnMountOrArgChange: false,
      refetchOnReconnect: false,
      skip: false,
    }
  );

  const { data: envs } = useGetEnvListQuery(
    {
      keys: [
        "PRIVACY_POLICY_URL",
        "TERM_OF_USE_URL",
      ],
    },
    {
      pollingInterval: 30 * 60 * 1000,
      refetchOnFocus: false,
      refetchOnMountOrArgChange: false,
      refetchOnReconnect: false,
      skip: false,
    }
  );

  const closeModal = useCallback(() => {
    dispatch(setOpenedModal(MODAL_NAME.NONE));
    sessionStorage.setItem("callOAuthCallback", "false");
  }, [dispatch, setOpenedModal]);

  const callbackFunc = useCallback(
    async (fromOAuth = false) => {
      console.log(
        "close callbackFunc called with callbackAfterLogin : ",
        callbackAfterLogin
      );

      dispatch(setOpenedModal("none"));
      dispatch(projectActions.setLoading(true));

      if (isMobile && router.query?.email) {
        router.replace("/");
      }

      if (
        router.query?.openSubscribe === "true" ||
        router.query?.openCheckout === "true"
      ) {
        // closeModal();
        // return;
        router.reload(); // 홈페이지 프라이싱 -> 대시보드 -> 로그인 -> 결제 하기위해 queryString 유지해야한다.
      }

      if (!callbackAfterLogin && !fromOAuth) {
        closeModal();
        router.reload();
        return;
      }

      const isGPT = router.query.isGPT === "true" ? true : false;
      const exportType = sessionStorage.getItem("exportType");
      const data = {
        ...removeBase64InProject(project),
        exportType,
        time,
        isGPT,
      };

      // console.log('handle login data: ', data)

      // console.log("!@#!@#!@# loginModal router", router.query)

      try {
        const newProject = { ...(project || {}) };
        if (project) {
          if (!project.userId || project.userId === "guest") {
            if (session?.data?.user?.id)
              newProject.userId = session?.data?.user?.id;
          }
        }
        console.log("LoginModal accountUserInfo init new", newProject);
        console.log("LoginModal accountUserInfo init old", project);

        if (newProject.automation && !newProject.automation?.isFinish) {
          const ep = new ExportProject({
            project: newProject,
            session,
            cookies,
            router,
            t,
            exportType,
            templateData: templateData?.data?.project,
            automationJobId: project?.automation?.jobId,
          });


          ep.onCompleted(ExportProgramName.exportEarlyPost, async () => {
            router.push("/dashboard");
          });

          await ep.run([ExportProgramName.exportEarlyPost]);
        } else {
          const ep = new ExportProject({
            project: newProject,
            session,
            cookies,
            router,
            t,
            exportType,
            templateData: templateData?.data?.project,
            automationJobId: project?.automation?.jobId,
          });



          ep.onFailed(ExportProgramName.export, async (res: any) => {
            if (res.error.code === ERR_CODE_4000) {
              // credit 부족
              const noticeModalPayload = {
                currentPlan: res.data.subscription,
                openedModal: MODAL_NAME.NOTICE,
                noticeTitle: t("Insufficient credits"),
                noticeExplains: [
                  `${t("Approximately")} ${
                    res.data.needCredit - res.data.credit
                  } ${t("credits are needed to export video.")}`,
                  t("Upgrade your plan now!"),
                ],
              };
              dispatch(projectActions.setNoticeModalCredit(noticeModalPayload));
            } else if (res.error.code === ERR_CODE_4004) {
              // scene 수 초과
              const payload = {
                currentPlan: res.data.subscription,
                openedModal: MODAL_NAME.SUBSCRIPTION_LIST,
              };

              if (!cookies?.first_page_view_pricing) {
                setCookie("first_page_view_pricing", true, { path: "/" });
                window.dataLayer.push({
                  event: "first_page_view_pricing",
                  location: "modal",
                });
              }

              if (window?.dataLayer?.push) {
                window.dataLayer.push({
                  event: "page_view_pricing",
                  location: "modal",
                });
              }

              dispatch(projectActions.setCurrentPlanAndOpenedModal(payload));
            }
          });

          ep.onCompleted(ExportProgramName.export, async (res: any) => {
            // console.log('login export exportType: ', exportType)
            if (exportType === "video") {
              console.log("login export video다 ");
              router.push("/dashboard");
            } else {
              const a = document.createElement("a");
              a.href = res.data.downloadUrl;
              a.download = res.data.downloadUrl.split("/").pop();
              document.body.appendChild(a);
              a.click();
              setTimeout(() => {
                a.remove();
              }, 2000);
            }
          });

          await ep.run([ExportProgramName.export]);
        }

        const exportHistoryResponse = await axios.get(
          "/api/dashboard/exportHistory?currentPage=1&perPage=10"
        );

        const generatedVideosCount =
          exportHistoryResponse.data.data.totalCount || 0;

        if (window?.dataLayer?.push) {
          window.dataLayer.push({
            event: "Video_generate_count",
            status_login: "log_in",
            nth_generating: generatedVideosCount + 1,
          });
        }
      } catch (error) {
        console.log("login export error: ", error);
      }

      dispatch(projectActions.setLoading(false));
      sessionStorage.setItem("callOAuthCallback", "false");
    },
    [
      callbackAfterLogin,
      dispatch,
      setOpenedModal,
      isMobile,
      router,
      project,
      time,
      closeModal,
      session,
      cookies,
      t,
      templateData?.data?.project,
      setCookie,
    ]
  );

  const closeModalWithRemoveQuery = () => {
    console.log("closeModalWithRemoveQuery");

    if (
      router?.query?.invite === "true" &&
      workspaceModal.type === "switchAccount"
    ) {
      router.reload();
    }

    if (currentStep !== "survey") {
      console.log("here22!! ");
      if (
        router.query?.openSubscribe === "true" ||
        router.query?.openCheckout === "true"
      ) {
        window.location.replace(window.location.pathname);
      }
      dispatch(setOpenedModal(MODAL_NAME.NONE));
      sessionStorage.setItem("callOAuthCallback", "false");

      if (session.status === "authenticated") router.reload();
    } else if (
      currentStep === "survey" &&
      !router?.asPath?.includes("/g/demo")
    ) {
      console.log("here!! ");
      callbackFunc(true);
    }

    // router.reload();

    // if( currentStep === "survey"){
    //   window.dataLayer.push({
    //     event: "survey_subscription_cancel",
    //   });
    // }
  };

  useEffect(() => {
    // console.log("close callbackCondition 11")
    if (!router.isReady) return;
    // console.log("close callbackCondition 22")
    const callOAuthCallback = sessionStorage.getItem("callOAuthCallback");
    const currentPath = router.asPath;
    const queryString = "signUp=true";
    const newPath = currentPath.includes("?")
      ? `${currentPath}&${queryString}`
      : `${currentPath}?${queryString}`;

    setRedirectPath(currentPath);
    setInputReferral(sessionStorage.getItem("referral") ?? "");
    // console.log('router.asPath: ', router.asPath)
    // console.log("close callbackCondition 33", router.asPath)
    const fromGoogle = router.query.from === "google";
    const fromAzure = router.query.from === "microsoft";
    const isCallback = callOAuthCallback === "true";
    const loggedIn = session.status === "authenticated";
    const callbackCondition =
      (fromGoogle || fromAzure) && isCallback && loggedIn;

    // console.log("close callbackCondition 44  callbackCondition :", callbackCondition, "isCallback : ",isCallback ,  "   fromGoogle : ",fromGoogle , "  loggedIn: ",loggedIn)
    if (callbackCondition) {
      console.log("close callbackCondition 55");
      callbackFunc(true);
    }
  }, [router, session, callbackFunc]);

  useEffect(() => {
    async function initCheckAccountUserInfo() {
      try {
        console.log(`LoginModal initCheckAccountUserInfo init `, {
          accountUserInfo: accountUserInfo?.id,
        });
        trigger();
        if (accountUserInfoData) {
          dispatch(
            dashboardActions.setAccountUserInfo({
              accountUserInfo: accountUserInfoData,
            })
          );
          if (
            accountUserInfoData?.plan
              ?.toLocaleLowerCase()
              ?.includes("enterprise")
          ) {
            if (!accountUserInfoData?.enterprise) {
              await checkEnterpriseCompany({
                userId: session?.data?.user?.id,
                NEXTAUTH_URL,
              })
                .then((result) => {
                  console.log(
                    `LoginModal initCheckAccountUserInfo checkEnterpriseCompany`
                  );
                  if (result?.success) {
                    console.log(
                      `LoginModal initCheckAccountUserInfo checkEnterpriseCompany success`
                    );
                    dispatch(
                      dashboardActions.setAccountUserInfo({
                        accountUserInfo: {
                          ...accountUserInfoData,
                          enterprise: true,
                        },
                      })
                    );
                  }
                })
                .catch((error) => {
                  console.error(
                    `LoginModal initCheckAccountUserInfo checkEnterpriseCompany error `,
                    { error }
                  );
                });
            }
          }

          if (
            accountUserInfoData?.plan?.toLocaleLowerCase()?.includes("team") ||
            accountUserInfoData?.plan?.toLocaleLowerCase()?.includes("pro") ||
            accountUserInfoData?.plan
              ?.toLocaleLowerCase()
              ?.includes("enterprise") ||
            accountUserInfoData?.enterprise
          ) {
            console.log(
              `LoginModal initCheckAccountUserInfo team, pro, enterprise plan`
            );
            dispatch(
              dashboardActions.setWorkspaceList({
                workspaceListData: accountUserInfoData?.workspaceList,
                total: accountUserInfoData?.workspaceListTotal,
                inviteWorkspaceLogs: accountUserInfoData?.inviteWorkspaceLogs,
              })
            );
          }
        }
      } catch (error) {
        console.error(`LoginModal initCheckAccountUserInfo error `, { error });
      }
    }

    if (session.status === "authenticated") {
      dispatch(projectActions.updateProjectUserId(session.data.user.email));
      if (accountUserInfo?._id === "") {
        initCheckAccountUserInfo();
      }
    }
  }, [session, dispatch, accountUserInfo, accountUserInfoData, NEXTAUTH_URL]);

  const [pageLoading, setPageLoading] = useState(false);

  return (
    <StyledModal
      width={440}
      open={openedModal === MODAL_NAME.LOGIN}
      centered
      footer={null}
      onCancel={closeModalWithRemoveQuery}
      zIndex={1003}
    >
      {decryptParams?.workspaceId ? (
        <AuthModalTemplate
          signInCallback={callbackFunc}
          landingPath={""}
          redirectPath={redirectPath}
          routerPushFunc={router.push}
          isLoginDefault={false}
          serviceId={env.data as string}
          referral={inputReferral}
          invite={true}
          companyId={decryptParams?.companyId || ""}
          workspaceId={decryptParams?.workspaceId || ""}
          setCurrentStep={setCurrentStep}
          currentPath={router?.asPath}
          workspaceName={decryptParams?.workspaceName || ""}
          accountUrl={ACCOUNT_URL}
          studioUrl={NEXTAUTH_URL}
          pageLoading={pageLoading}
          setPageLoading={setPageLoading}
          privacyPolicyUrl={envs?.PRIVACY_POLICY_URL}
          termOfUseUrl={envs?.TERM_OF_USE_URL}
          t={t}
          Trans={Trans}
        />
      ) : (
        <AuthModalTemplate
          signInCallback={callbackFunc}
          landingPath={""}
          redirectPath={redirectPath}
          routerPushFunc={router.push}
          isLoginDefault={!router?.asPath.includes("/editor")}
          serviceId={env.data as string}
          referral={inputReferral}
          currentPath={router?.asPath}
          setCurrentStep={setCurrentStep}
          accountUrl={ACCOUNT_URL}
          studioUrl={NEXTAUTH_URL}
          pageLoading={pageLoading}
          setPageLoading={setPageLoading}
          privacyPolicyUrl={envs?.PRIVACY_POLICY_URL}
          termOfUseUrl={envs?.TERM_OF_USE_URL}
          t={t}
          Trans={Trans}
        />
      )}
    </StyledModal>
  );
};
